/**
 * Internationalization (i18n) utility functions.
 */

type TranslatableContent = string | { [locale: string]: string | undefined } | null | undefined;

/**
 * Gets the translation for a given piece of content based on the desired locale.
 * Falls back to English ('en') or a provided fallback string if the desired locale is not found.
 *
 * @param content The content, which can be a string or an object with locale keys.
 * @param locale The desired locale (e.g., 'en', 'fr'). Defaults to 'en'.
 * @param fallback A fallback string to return if no suitable translation is found. Defaults to an empty string.
 * @returns The translated string or the fallback string.
 */
export const getTranslation = (content: TranslatableContent, locale: string = 'en', fallback: string = ''): string => {
  if (typeof content === 'string') {
    return content;
  }

  if (typeof content === 'object' && content !== null) {
    if (content[locale]) {
      return content[locale]!;
    }
    if (locale !== 'en' && content['en']) {
      return content['en']!;
    }
    // Fall back to any available non-empty locale value
    const anyValue = Object.values(content).find((v) => typeof v === 'string' && v.length > 0);
    if (anyValue) {
      return anyValue;
    }
  }

  return fallback;
};
